import { useState, useEffect } from 'react';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { DataGrid, itIT, GridToolbar, useGridApiRef } from '@mui/x-data-grid';		//https://mui.com/x/react-data-grid/ [MIT version]
import { Box, Alert, Button, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, TextField, MenuItem, Card, CardHeader, CardContent } from '@mui/material';
import { Link } from 'react-router-dom';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import FastTextField from '../FastTextField.js';

import CommentIcon from '@mui/icons-material/Comment';
import EuroIcon from '@mui/icons-material/Euro';
import loadingGif from '../../Assets/loading.gif';

import Api from '../../Services/Api.js';
import { PAGE_SIZE, pageSizeOptions, timeTableIntervals as optionsTimeTableIntervals } from '../../Config/constants.js';
import moment from 'moment';
import ActionButton from '../ActionButton.js';

function ReportCustomersWithoutPayment() {

	const apiRef = useGridApiRef();	//https://mui.com/x/api/data-grid/
	const DEFAULT_VALUE = "0";

	const [rows, setRows] = useState([]);
	const [displayAlert, setDisplayAlert] = useState(0);
	const [displayDialog, setDisplayDialog] = useState(0);
	const [displayDialogContent, setDisplayDialogContent] = useState(null);
	const [dialogTitle, setDialogTitle] = useState(null);
	const [lastUpdate, setLastUpdate] = useState(null);
	const [days, setDays] = useState(DEFAULT_VALUE);
	const [loading, setLoading] = useState(false);


	const formik = useFormik({
		initialValues: {
			id: "",
			payment_post_motivations: "",
		},
		validationSchema: Yup.object({
			payment_post_motivations: Yup.string().required('Il campo è obbligatorio').max(250, 'Lunghezza massima 250 caratteri'),
		}),
		onSubmit: values => {
			setLoading(true);
			if (window.confirm("Confermare la ricezione del pagamento sul cliente indicato?")) {
				approvePostPayments({
					id: values.id,
					payment_post_motivations: values.payment_post_motivations
				});
			}
			setLoading(false);
		},
	});

	const showFullNote = (notes) => {
		return <>
			<p>{notes}</p>
		</>;
	};
	const showPostPaymentForm = (id) => {
		formik.setFieldValue('id', id);
		formik.setFieldValue('payment_post_motivations', '');

		return <>
			<form autoComplete="off" noValidate onSubmit={formik.handleSubmit}>
				<Card>
					<CardHeader title="Inserisci dettagli del pagamento" subheader="Per poter salvare il pagamento come gestito è necessario specificare i dettagli dell’avvenuto pagamento o della perdita del credito" />
					<CardContent sx={{ pt: 0 }}>
						<div key="payment_post_motivations" className="row styled">
							<div key="payment_post_motivations" className="col-md-12">
								<FastTextField formik={formik} field="payment_post_motivations" multiline rows={4} />
								<FastTextField formik={formik} field="id" hidden={true} type="hidden" />
							</div>
						</div>
						<div className="row styled">
							<div className="col">
								{loading ? <>
									{displayAlert === -1 && (
										<Alert severity="error">Si è verificato un errore imprevisto. Riprovare più tardi</Alert>
									)}
									{displayAlert === 0 && (
										<img className="pl-3" src={loadingGif} alt="Loading..."/>
									)}
									{displayAlert === 1 && (
										<Alert severity="success">Dati salvati con successo!</Alert>
									)}
								</> : <>
									<Button variant="contained" type="submit">Conferma</Button>
								</>}
							</div>
						</div>
					</CardContent>
				</Card>
			</form>
		</>;
	};
	
	const columns = [
		{
			field: 'customer',
			headerName: 'Cliente',
			flex: 1,
			valueGetter: (params) => {
				return `${params.row.surname} ${params.row.name}`;
			},
		},
		{
			field: 'installation',
			headerName: 'Impianto',
			flex: 2,
			valueGetter: (params) => {
				return `${params.row.brand} ${params.row.model} ${params.row.unique_code}`;
			},
		},
		{
			field: 'appointment_at',
			headerName: 'Appuntamento',
			flex: 2,
			type: 'date',
			valueGetter: (params) => params.row.appointment_at ? new Date(params.row.appointment_at) : null,
			valueFormatter: (params) => params.value ? params.value.toLocaleDateString('it-IT') : '',
		},
		{
			field: 'price',
			headerName: 'Importo',
			type: 'number',
			valueFormatter: (params) => params.value != null ? new Intl.NumberFormat('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(params.value) : '',
		},
		{
			field: 'payment_notes',
			headerName: 'Note',
			flex: 2,
			valueGetter: (params) => {
				return `${params.row.payment_notes.substring(0, 25)}...`;
			},
		},
		{
			field: 'id',
			headerName: 'Azioni',
			flex: 1,
			renderCell: (params) => {
				function onShow(e) {
					e.stopPropagation(); // don't select this row after clicking
					setDisplayDialogContent(showFullNote(params.row.payment_notes));
					setDialogTitle('Nota estesa');
					setDisplayDialog(1);
				};
				function approvePostPayment(e) {
					e.stopPropagation(); // don't select this row after clicking
					setDisplayDialogContent(showPostPaymentForm(params.row.id));
					setDialogTitle('Gestisci Pagamento');
					setDisplayDialog(1);
				};
				return <>
					<ActionButton title={"Visualizza Nota estesa"} onClick={onShow}>
						<CommentIcon />
					</ActionButton>
					<ActionButton title={"Gestisci Pagamento"} onClick={approvePostPayment}>
						<EuroIcon />
					</ActionButton>
				</>
			}
		},
	]

	const handleClose = () => {
		setDisplayDialog(0);
		setDisplayDialogContent(null);
		setDisplayAlert(0);
		setDialogTitle(null);
	};

	function arrayIntervalsResponse() {
		return (
			[DEFAULT_VALUE, ...optionsTimeTableIntervals].map((interval) => (
				<MenuItem key={interval} value={interval}>
					{interval === '0' ? 'Nessun filtro' : `${interval} giorni`}
				</MenuItem>
			))
		);
	}

	function extraFilter(selectedValue) {
		setDays(selectedValue);
	}

	function approvePostPayments(data) {
		try {
			Api.post({endpoint: `report/approve-post-payment`, body: data, contentType : 'application/json'})
				.then(response => {
					setDisplayDialog(1);
					setDisplayAlert(1);
					setDisplayDialogContent(null);
					setLastUpdate(Date.now()+1);
				})
				.catch(error => {
					console.log(JSON.stringify(error));
					setDisplayDialog(-1);
					setDisplayAlert(-1);
				});
		} catch (error) {
			console.log(JSON.stringify(error));
			setDisplayDialog(-1);
			setDisplayAlert(-1);
		}
	};

	useEffect(() => {
		try {
			Api.get({endpoint: `report/customers-without-payment/${days}`, contentType : 'application/json'})
			.then(response => {
				setRows(response.data.list);
			})
			.catch(error => {
				console.log(JSON.stringify(error));
				setDisplayAlert(-1);
			});
		} catch (error) {
			console.log(JSON.stringify(error));
			setDisplayAlert(-1);
		}
	}, [lastUpdate, days]);	// eslint-disable-line react-hooks/exhaustive-deps

	const theme = createTheme({
			palette: {
				primary: { main: '#11cb5f' },
			},
		},
		itIT,
	);

	  
	return (
		<>
			<h5>Report Clienti senza Pagamento</h5>

			<div className="row styled">
				<div className="col-md-2">
					<TextField
						fullWidth
						label="Intervallo di visualizzazione"
						name="interval"
						className="styled"
						select
						defaultValue = {DEFAULT_VALUE}
						InputProps={{
							style: {color: theme.palette.primary.darkMain}
						}}
						onChange={(e) => {
							extraFilter(e.target.value);
						}}
					>
						{arrayIntervalsResponse()}
					</TextField>
				</div>
				<div className="col-md-10">
					<p style={{fontSize: '0.8em'}}>
						{days && <>
						{days === '0' ? <span>Nessun filtro</span> : <span>Dal: {moment().subtract(days, 'days').format('DD/MM/YYYY')} - Al: {moment().format('DD/MM/YYYY')}</span>}
						</>}
					</p>
				</div>
			</div>

			<Box sx={{ width: '100%', minWidth: '800px' }}>
				<ThemeProvider theme={theme}>
					<DataGrid
						sx={{ minHeight: '300px' }}
						rows={rows}
						columns={columns}						
						initialState={{
							pagination: {
								paginationModel: {
									pageSize: PAGE_SIZE,
								},
							},
						}}
						apiRef={apiRef}
						slots={{
							toolbar: GridToolbar,
						}}
						slotProps={{
							columnsPanel: {
								disableHideAllButton: true,
							},
							toolbar: {
								showQuickFilter: true,
							},
						}}
						pageSizeOptions={pageSizeOptions}
					/>
					<div className="mt-2">
						<Link to={'/cms/reports'} >
							<Button variant="contained">Torna alla lista</Button>
						</Link>
					</div>
				</ThemeProvider>
				{displayAlert === -1 && (
					<Alert severity="error">Si è verificato un errore imprevisto. Riprovare più tardi</Alert>
				)}
			</Box>

			<Dialog
				open={displayDialog !== 0}
				onClose={handleClose}
				aria-labelledby="alert-dialog-title"
				aria-describedby="alert-dialog-description"
			>
				{displayDialogContent && displayDialog !== -1? (
					<>
						<DialogTitle id="alert-dialog-title">
							{dialogTitle}
						</DialogTitle>
						<DialogContent>
							<DialogContentText id="alert-dialog-description">
								<div>{displayDialogContent}</div>
							</DialogContentText>
						</DialogContent>
					</>
				) : (
					<>
						<DialogTitle id="alert-dialog-title">
							{displayDialog === 1 && "Operazione completata"}
							{displayDialog === -1 && "Errore"}
						</DialogTitle>
						<DialogContent>
								<DialogContentText id="alert-dialog-description">
									{displayDialog === 1 && "Operazione completata con successo!"}
									{displayDialog === -1 && "Si è verificato un errore inatteso"}
								</DialogContentText>
						</DialogContent>
					</>
				)}
				<DialogActions>
					<Button onClick={handleClose}>Chiudi</Button>
				</DialogActions>
			</Dialog>
		</>
	);
}

export default ReportCustomersWithoutPayment;